import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { FileText, LogOut } from "lucide-react";
import { useNavigate } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const parseYouTubeUrl = (url) => {
  const regExp = /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*/;
  const match = url.match(regExp);
  return (match && match[7].length === 11) ? match[7] : null;
};

const parseVimeoUrl = (url) => {
  const regExp = /(?:vimeo)\.com.*(?:videos|video|channels|)\/([\d]+)/i;
  const match = url.match(regExp);
  return match ? match[1] : null;
};

const renderEmbeddedLink = (link, index) => {
  const youtubeId = parseYouTubeUrl(link);
  if (youtubeId) {
    return (
      <div key={index} className="my-4">
        <iframe
          width="100%"
          height="400"
          src={`https://www.youtube.com/embed/${youtubeId}`}
          title="YouTube video player"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          style={{ borderRadius: '8px' }}
        ></iframe>
      </div>
    );
  }

  const vimeoId = parseVimeoUrl(link);
  if (vimeoId) {
    return (
      <div key={index} className="my-4">
        <iframe
          src={`https://player.vimeo.com/video/${vimeoId}`}
          width="100%"
          height="400"
          frameBorder="0"
          title={`Vimeo video ${vimeoId}`}
          allow="autoplay; fullscreen; picture-in-picture"
          allowFullScreen
          style={{ borderRadius: '8px' }}
        ></iframe>
      </div>
    );
  }

  return (
    <a
      key={index}
      href={link}
      target="_blank"
      rel="noopener noreferrer"
      className="block text-blue-500 hover:underline my-2"
    >
      {link}
    </a>
  );
};

export default function NotesPage({ setIsAuthenticated }) {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedNotes, setExpandedNotes] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    fetchNotes();
  }, []);

  const fetchNotes = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/signals-app/notes`);
      setNotes(response.data);
    } catch (error) {
      console.error('Error fetching notes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('signals_token');
    localStorage.removeItem('signals_user_email');
    if (setIsAuthenticated) setIsAuthenticated(false);
    navigate('/login');
  };

  const toggleNoteExpand = (noteId) => {
    setExpandedNotes(prev => ({
      ...prev,
      [noteId]: !prev[noteId]
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
        <div className="text-lg text-white">Loading notes...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      <div className="container mx-auto p-6 max-w-5xl">
        {/* Header */}
        <div className="mb-8 flex justify-between items-center">
          <div>
            <h1 className="text-4xl font-bold mb-2 text-white">Market Notes & Updates</h1>
            <p className="text-gray-300">Latest insights and analysis from Savage AI</p>
          </div>
          <div className="flex gap-3">
            <Button 
              variant="outline" 
              onClick={() => navigate('/signals')}
              style={{
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                color: 'white',
                border: 'none'
              }}
            >
              View Signals
            </Button>
            <Button 
              variant="outline" 
              onClick={handleLogout}
              style={{
                background: 'rgba(255, 255, 255, 0.1)',
                color: 'white',
                border: '1px solid rgba(255, 255, 255, 0.2)'
              }}
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>

        {/* Notes List */}
        <div className="space-y-6">
          {notes.length === 0 ? (
            <Card style={{ background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
              <CardContent className="py-12 text-center">
                <FileText className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                <p className="text-gray-400 text-lg">No notes available yet</p>
                <p className="text-gray-500 text-sm mt-2">Check back later for market insights and updates</p>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Pinned Notes Section */}
              {notes.some(n => n.pinned) && (
                <div className="mb-8">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="h-8 w-1 bg-gradient-to-b from-purple-500 to-purple-700 rounded-full"></div>
                    <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                      📌 Pinned Notes
                    </h2>
                  </div>
                  <div className="space-y-6">
                    {notes.filter(n => n.pinned).map((note) => (
                      <Card 
                        key={note.note_id} 
                        style={{ 
                          background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.1) 0%, rgba(99, 102, 241, 0.1) 100%)',
                          border: '2px solid rgba(139, 92, 246, 0.4)',
                          backdropFilter: 'blur(10px)'
                        }}
                      >
                        <CardHeader>
                          <div className="flex justify-between items-start">
                            <div>
                              <div className="flex items-center gap-2 mb-2">
                                <span className="text-2xl">📌</span>
                                <span className="text-purple-400 text-sm font-semibold uppercase tracking-wide">Pinned</span>
                              </div>
                              <CardTitle className="text-white">
                                {new Date(note.created_at).toLocaleDateString('en-US', {
                                  year: 'numeric',
                                  month: 'long',
                                  day: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </CardTitle>
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent>
                          {/* Note Preview - Always visible */}
                          <div 
                            className="cursor-pointer hover:bg-white/5 p-4 rounded-lg transition-all"
                            onClick={() => toggleNoteExpand(note.note_id)}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex-1">
                                <div className="text-white font-medium text-lg line-clamp-2">
                                  {note.content.split('\n')[0].substring(0, 120)}
                                  {note.content.length > 120 && '...'}
                                </div>
                                <div className="text-sm text-purple-300 mt-2 flex items-center gap-3">
                                  {(note.image_url || (note.image_urls && note.image_urls.length > 0)) && (
                                    <span>🖼️ {note.image_urls?.length > 1 ? `${note.image_urls.length} Images` : 'Image'}</span>
                                  )}
                                  {note.links && note.links.length > 0 && <span>🔗 {note.links.length} link(s)</span>}
                                  <span className="text-purple-400">• Click to {expandedNotes[note.note_id] ? 'collapse' : 'expand'}</span>
                                </div>
                              </div>
                              <div className="text-purple-300 ml-4 text-2xl">
                                {expandedNotes[note.note_id] ? '▼' : '▶'}
                              </div>
                            </div>
                          </div>

                          {/* Expanded Content */}
                          {expandedNotes[note.note_id] && (
                            <div className="mt-6 pt-6 border-t border-purple-500/20">
                              <div 
                                className="whitespace-pre-wrap mb-4 text-gray-200 text-lg leading-relaxed"
                                style={{ lineHeight: '1.8' }}
                              >
                                {note.content}
                              </div>

                              {/* Multiple images (new format) */}
                              {note.image_urls && note.image_urls.length > 0 && (
                                <div className="space-y-4 mb-6">
                                  {note.image_urls.map((imgUrl, imgIndex) => (
                                    <img
                                      key={imgIndex}
                                      src={imgUrl.startsWith('http') ? imgUrl : `${BACKEND_URL}${imgUrl}`}
                                      alt={`Note attachment ${imgIndex + 1}`}
                                      className="max-w-full rounded-lg shadow-xl"
                                      style={{ maxHeight: '500px', objectFit: 'contain' }}
                                      onError={(e) => {
                                        console.error('Image failed to load:', imgUrl);
                                        e.target.style.display = 'none';
                                      }}
                                    />
                                  ))}
                                </div>
                              )}
                              {/* Legacy single image (backward compatibility) */}
                              {note.image_url && (!note.image_urls || note.image_urls.length === 0) && (
                                <img
                                  src={note.image_url.startsWith('http') ? note.image_url : `${BACKEND_URL}${note.image_url}`}
                                  alt="Note"
                                  className="max-w-full rounded-lg mb-6 shadow-xl"
                                  style={{ maxHeight: '500px', objectFit: 'contain' }}
                                  onError={(e) => {
                                    console.error('Image failed to load:', note.image_url);
                                    e.target.style.display = 'none';
                                  }}
                                />
                              )}

                              {note.links && note.links.length > 0 && (
                                <div className="space-y-4 mt-6">
                                  {note.links.map((link, idx) => renderEmbeddedLink(link, idx))}
                                </div>
                              )}
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}

              {/* Regular Notes Section */}
              {notes.some(n => !n.pinned) && (
                <div>
                  {notes.some(n => n.pinned) && (
                    <div className="flex items-center gap-2 mb-4">
                      <div className="h-8 w-1 bg-gradient-to-b from-blue-500 to-blue-700 rounded-full"></div>
                      <h2 className="text-2xl font-bold text-white">Recent Notes</h2>
                    </div>
                  )}
                  <div className="space-y-6">
                    {notes.filter(n => !n.pinned).map((note) => (
              <Card 
                key={note.note_id} 
                style={{ 
                  background: 'rgba(255, 255, 255, 0.05)', 
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  backdropFilter: 'blur(10px)'
                }}
              >
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <CardTitle className="text-white">
                      {new Date(note.created_at).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  {/* Note Preview - Always visible */}
                  <div 
                    className="cursor-pointer hover:bg-white/5 p-4 rounded-lg transition-all border border-blue-500/30 hover:border-blue-500"
                    onClick={() => toggleNoteExpand(note.note_id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="text-white font-medium text-lg line-clamp-2">
                          {note.content.split('\n')[0].substring(0, 120)}
                          {note.content.length > 120 && '...'}
                        </div>
                        <div className="text-sm text-gray-400 mt-2 flex items-center gap-3">
                          {(note.image_url || (note.image_urls && note.image_urls.length > 0)) && (
                            <span>🖼️ {note.image_urls?.length > 1 ? `${note.image_urls.length} Images` : 'Image'}</span>
                          )}
                          {note.links && note.links.length > 0 && (
                            <>
                              {note.links.some(link => parseYouTubeUrl(link) || parseVimeoUrl(link)) && <span>🎥 Video</span>}
                              {note.links.some(link => !parseYouTubeUrl(link) && !parseVimeoUrl(link)) && <span>🔗 Link</span>}
                            </>
                          )}
                          <span className="text-blue-400 font-semibold">👉 {expandedNotes[note.note_id] ? 'Click to collapse' : 'Click to view full content'}</span>
                        </div>
                      </div>
                      <div className="text-blue-400 ml-4 text-2xl font-bold">
                        {expandedNotes[note.note_id] ? '▼' : '▶'}
                      </div>
                    </div>
                  </div>

                  {/* Expanded Content */}
                  {expandedNotes[note.note_id] && (
                    <div className="mt-6 pt-6 border-t border-gray-700">
                      {/* Note Content */}
                      <div 
                        className="whitespace-pre-wrap mb-4 text-gray-200 text-lg leading-relaxed"
                        style={{ lineHeight: '1.8' }}
                      >
                        {note.content}
                      </div>

                      {/* Multiple images (new format) */}
                      {note.image_urls && note.image_urls.length > 0 && (
                        <div className="space-y-4 mb-6">
                          {note.image_urls.map((imgUrl, imgIndex) => (
                            <img
                              key={imgIndex}
                              src={imgUrl.startsWith('http') ? imgUrl : `${BACKEND_URL}${imgUrl}`}
                              alt={`Note attachment ${imgIndex + 1}`}
                              className="max-w-full rounded-lg shadow-xl"
                              style={{ maxHeight: '500px', objectFit: 'contain' }}
                              onError={(e) => {
                                console.error('Image failed to load:', imgUrl);
                                e.target.style.display = 'none';
                              }}
                            />
                          ))}
                        </div>
                      )}
                      {/* Legacy single image (backward compatibility) */}
                      {note.image_url && (!note.image_urls || note.image_urls.length === 0) && (
                        <img
                          src={note.image_url.startsWith('http') ? note.image_url : `${BACKEND_URL}${note.image_url}`}
                          alt="Note"
                          className="max-w-full rounded-lg mb-6 shadow-xl"
                          style={{ maxHeight: '500px', objectFit: 'contain' }}
                          onError={(e) => {
                            console.error('Image failed to load:', note.image_url);
                            e.target.style.display = 'none';
                          }}
                        />
                      )}

                      {/* Embedded Links/Videos */}
                      {note.links && note.links.length > 0 && (
                        <div className="space-y-4 mt-6">
                          {note.links.map((link, idx) => renderEmbeddedLink(link, idx))}
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
